import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import useFlag from 'lane-shared/hooks/useFlag';
import { FeatureFlag } from 'constants-flags';
import getIntegrationsManifest from 'lane-shared/graphql/query/getIntegrationsManifest';
import { getVisitorProviders } from 'domains-visitor-validation';
import { IntegrationDomain } from 'graphql-query-contracts';
import type {
  GetIntegrationsManifestQuery,
  GetIntegrationsManifestQueryVariables,
} from 'graphql-query-contracts';

/**
 * The building's visitor integration providers, whose rules the visitor invite
 * form and bulk upload apply (see validateVisitorInput). Empty unless the flag
 * is on, otherwise whatever the IGS integrations manifest lists for the
 * building's VISITORS domain. The server makes the same check against IGS
 * before creating passes.
 *
 * Not fetched while the flag is off. Empty while loading or on error; the
 * server re-checks every submission, so a slow or failed lookup never blocks
 * the form.
 *
 * `loading` lets importers wait for the answer: rows are only validated when
 * they are uploaded, so a CSV imported before the answer arrives would skip
 * the rules.
 */
export const useVisitorIntegrationProviders = (channelId?: string) => {
  const isIntegrationValidationEnabled = useFlag<boolean>(
    FeatureFlag.VisitorManagementKastleNameValidation,
    false
  );

  const { data, loading } = useQuery<
    GetIntegrationsManifestQuery,
    GetIntegrationsManifestQueryVariables
  >(getIntegrationsManifest, {
    variables: {
      channelId: channelId!,
      domain: IntegrationDomain.DomainVisitors,
    },
    skip: !channelId || !isIntegrationValidationEnabled,
    // Without this an IGS error would surface as a form error; the server
    // re-checks, so the form just applies no rules.
    errorPolicy: 'all',
  });

  const integrations = data?.getIntegrationsManifest?.integrations;
  const providers = useMemo(
    () => getVisitorProviders(integrations),
    [integrations]
  );

  return { providers, loading };
};
